import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';

interface QuantityOption {
  quantity: number;
  unit: string;
  notes?: string;
}

interface QuantitiesMatrixDirectProps {
  specifications: any[];
  deliveryLocations: any[];
  quantityOptions: Record<string, QuantityOption[]>;
  matrix: any;
  onQuantityChange: (specId: string, locationId: string, quantity: number) => void;
  readOnly?: boolean;
}

export function QuantitiesMatrixDirect({ 
  specifications, 
  deliveryLocations,
  quantityOptions,
  matrix,
  onQuantityChange,
  readOnly = false
}: QuantitiesMatrixDirectProps) {
  const getQuantity = (specId: string, locationId: string): number => {
    return matrix[specId]?.[locationId]?.quantity || 0;
  };

  const getSpecificationTotal = (specId: string): number => {
    if (!matrix[specId]) return 0;
    return Object.values(matrix[specId] as Record<string, any>).reduce(
      (sum: number, item: any) => sum + (Number(item.quantity) || 0), 
      0
    );
  };

  // Get the target quantity for a specification (from the single quantity option)
  const getTargetQuantity = (specId: string): number => {
    const options = quantityOptions[specId];
    if (!options || options.length === 0) return 0;
    return options[0].quantity;
  };

  // Get the unit for a specification
  const getUnit = (specId: string): string => {
    const options = quantityOptions[specId];
    if (!options || options.length === 0) return 'units';
    return options[0].unit;
  };

  const isSpecificationComplete = (specId: string): boolean => {
    const total = getSpecificationTotal(specId);
    const target = getTargetQuantity(specId);
    return total === target;
  };

  const isSpecificationOverAllocated = (specId: string): boolean => {
    const total = getSpecificationTotal(specId);
    const target = getTargetQuantity(specId);
    return total > target;
  };

  const allSpecificationsComplete = () => {
    return specifications.every(spec => isSpecificationComplete(spec.id));
  };

  const validationStatus = allSpecificationsComplete();

  return (
    <div className="space-y-4">
      {!readOnly && !validationStatus && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Distribute quantities for all specifications.</strong> Each row must match its target quantity.
          </AlertDescription>
        </Alert>
      )}

      {!readOnly && validationStatus && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            All quantities properly distributed! Each specification matches its target.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Quantity Distribution by Location</CardTitle>
          <CardDescription>
            Enter the exact quantity to deliver to each location. Each row must match the total quantity defined for that specification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border p-3 bg-muted text-left font-semibold sticky left-0 bg-background z-10 min-w-[200px]">
                    Specification
                  </th>
                  <th className="border p-3 bg-muted text-center font-semibold min-w-[100px]">
                    Target
                  </th>
                  {deliveryLocations.map(location => (
                    <th key={location.id} className="border p-3 bg-muted text-center min-w-[140px]">
                      <div className="font-semibold">{location.delivery_name}</div>
                      <div className="text-xs text-muted-foreground mt-1 font-normal">
                        {location.city}, {location.state_province}
                      </div>
                    </th>
                  ))}
                  <th className="border p-3 bg-muted text-center font-semibold min-w-[120px]">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {specifications.map(spec => {
                  const total = getSpecificationTotal(spec.id);
                  const target = getTargetQuantity(spec.id);
                  const unit = getUnit(spec.id);
                  const isComplete = isSpecificationComplete(spec.id);
                  const isOverAllocated = isSpecificationOverAllocated(spec.id);
                  
                  return (
                    <tr key={spec.id} className={isOverAllocated ? 'bg-red-50' : isComplete ? 'bg-green-50' : ''}>
                      <td className="border p-3 sticky left-0 bg-background z-10">
                        <div className="font-medium">{spec.specification_name}</div>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {spec.specification_type.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="border p-3 text-center">
                        <div className="font-semibold">{target.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">{unit}</div>
                      </td>
                      {deliveryLocations.map(location => (
                        <td key={location.id} className="border p-2">
                          <div className="flex flex-col items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={getQuantity(spec.id, location.id) || ''}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                onQuantityChange(spec.id, location.id, value);
                              }}
                              className="w-full text-center"
                              placeholder="0"
                              disabled={readOnly}
                            />
                            <span className="text-xs text-muted-foreground">{unit}</span>
                          </div>
                        </td>
                      ))}
                      <td className={`border p-3 text-center font-semibold ${
                        isOverAllocated ? 'bg-red-200 text-red-900' : 
                        isComplete ? 'bg-green-200 text-green-900' : 
                        'bg-yellow-100 text-yellow-900'
                      }`}>
                        {total.toLocaleString()} / {target.toLocaleString()}
                        {!isComplete && !isOverAllocated && total < target && (
                          <div className="text-xs mt-1">
                            ({(target - total).toLocaleString()} remaining)
                          </div>
                        )}
                        {isOverAllocated && (
                          <div className="text-xs mt-1">
                            ({(total - target).toLocaleString()} over!)
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div className="text-sm text-muted-foreground space-y-2">
                <p><strong>How to use quantity distribution:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Enter the exact quantity to deliver to each location</li>
                  <li>The sum across all locations must equal the target quantity</li>
                  <li>Green rows indicate correct distribution (matches target)</li>
                  <li>Yellow rows indicate incomplete distribution (&lt; target)</li>
                  <li>Red rows indicate over-allocation (&gt; target, fix required)</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
